import type { PnLResponse } from '@/shared/api';

export interface MonthRow {
  month: string;
  einnahmen: number;
  variableKosten: number;
  laufendeKosten: number;
}

/**
 * Der Server liefert die Monatsschlüssel ASCII-portabel („Maerz"). Ein `slice(0, 3)` darauf ergab
 * auf der Achse „Mae" — der Schlüssel ist eine Transportform, kein Achsenbeschrifter.
 */
const MONTH_ABBREVIATIONS: Readonly<Record<string, string>> = {
  Januar: 'Jan',
  Februar: 'Feb',
  Maerz: 'Mär',
  April: 'Apr',
  Mai: 'Mai',
  Juni: 'Jun',
  Juli: 'Jul',
  August: 'Aug',
  September: 'Sep',
  Oktober: 'Okt',
  November: 'Nov',
  Dezember: 'Dez',
};

/**
 * Die Provisionen haben im Vertrag keine Monatsaufteilung — sie stecken nur als Summe im
 * Nettoergebnis. Sie wurden bisher als Balken mit dem Wert 0 gezeichnet: die Legende behauptete
 * damit eine Kostenart, zu der das Diagramm nichts sagt. Die Zahl selbst bleibt unverändert, sie
 * steht im Faktenband.
 */
export function buildCashflowSeries(pnl: PnLResponse): MonthRow[] {
  return Object.keys(pnl.income.byMonth).map((month): MonthRow => {
    const totalExpense = pnl.expenses.byMonth[month] ?? 0;
    const recurring = pnl.expenses.recurring.byMonth[month] ?? 0;
    return {
      month: MONTH_ABBREVIATIONS[month] ?? month,
      einnahmen: (pnl.income.byMonth[month] ?? 0) + (pnl.income.otherIncomeByMonth[month] ?? 0),
      variableKosten: -Math.abs(totalExpense - recurring),
      laufendeKosten: -Math.abs(recurring),
    };
  });
}

export function hasCashflow(rows: MonthRow[]): boolean {
  return rows.some(
    (row) => row.einnahmen !== 0 || row.variableKosten !== 0 || row.laufendeKosten !== 0,
  );
}
